import { professionsOf } from "../../utils/professionScope";
import React, { useState, useEffect } from "react";
import Modal, {
  ModalPrimaryButton,
  ModalSecondaryButton,
} from "./Modal";
import LoadingSpinner from "./LoadingSpinner";
import authService from "../../services/authService"; // Already exists in backend, import here
// Helper functions for profession visual styling
const categorizeProfession = (type) => {
  const t = (type || "").toString().toUpperCase();
  if (t.includes("APP") || t.includes("PLATTFORM") || t.includes("INFORMATIK"))
    return "IT";
  if (t.includes("MEDIAMATIK")) return "MEDIAMATIK";
  if (t.includes("KAUF") || t.includes("KV")) return "KV";
  if (t.includes("DETAIL")) return "DETAILHANDEL";
  if (t.includes("KUNDENDIALOG")) return "KUNDENDIALOG";
  // Fallback: If KV student needs Detail instructor or vice-versa, can be customized here
  // But user requested strict separation
  return "OTHER";
};

const getThemeClasses = (category) => {
  switch (category) {
    case "IT":
      return {
        iconBg: "bg-blue-100",
        iconText: "text-blue-600",
        focusRing: "focus:ring-blue-100",
        focusBorder: "focus:border-blue-400",
        selectedBg: "bg-blue-50/50",
        selectedBorder: "border-blue-500",
        selectedText: "text-blue-700",
        hoverText: "group-hover:text-blue-600",
        checkboxBg: "bg-blue-500",
        checkboxBorder: "border-blue-500",
        checkboxBorderHover: "group-hover:border-blue-300",
        buttonText: "text-blue-500 hover:text-blue-700 hover:bg-blue-50",
        confirmBtn:
          "bg-gradient-to-r from-blue-500 to-blue-600 hover:to-blue-700",
      };
    case "MEDIAMATIK":
      return {
        iconBg: "bg-purple-100",
        iconText: "text-purple-600",
        focusRing: "focus:ring-purple-100",
        focusBorder: "focus:border-purple-400",
        selectedBg: "bg-purple-50/50",
        selectedBorder: "border-purple-500",
        selectedText: "text-purple-700",
        hoverText: "group-hover:text-purple-600",
        checkboxBg: "bg-purple-500",
        checkboxBorder: "border-purple-500",
        checkboxBorderHover: "group-hover:border-purple-300",
        buttonText: "text-purple-500 hover:text-purple-700 hover:bg-purple-50",
        confirmBtn:
          "bg-gradient-to-r from-purple-500 to-purple-600 hover:to-purple-700",
      };
    case "KV":
      return {
        iconBg: "bg-gray-500/10",
        iconText: "text-gray-500",
        focusRing: "focus:ring-gray-100",
        focusBorder: "focus:border-gray-400",
        selectedBg: "bg-gray-50/50",
        selectedBorder: "border-gray-500",
        selectedText: "text-gray-700",
        hoverText: "group-hover:text-gray-600",
        checkboxBg: "bg-gray-500",
        checkboxBorder: "border-gray-500",
        checkboxBorderHover: "group-hover:border-gray-300",
        buttonText: "text-gray-500 hover:text-gray-700 hover:bg-gray-50",
        confirmBtn:
          "bg-gradient-to-r from-gray-500 to-gray-600 hover:to-gray-700",
      };
    case "DETAILHANDEL":
      return {
        iconBg: "bg-orange-100",
        iconText: "text-orange-600",
        focusRing: "focus:ring-orange-100",
        focusBorder: "focus:border-orange-400",
        selectedBg: "bg-orange-50/50",
        selectedBorder: "border-orange-500",
        selectedText: "text-orange-700",
        hoverText: "group-hover:text-orange-600",
        checkboxBg: "bg-orange-500",
        checkboxBorder: "border-orange-500",
        checkboxBorderHover: "group-hover:border-orange-300",
        buttonText: "text-orange-500 hover:text-orange-700 hover:bg-orange-50",
        confirmBtn:
          "bg-gradient-to-r from-orange-500 to-orange-600 hover:to-orange-700",
      };
    case "KUNDENDIALOG":
      return {
        iconBg: "bg-green-100",
        iconText: "text-green-600",
        focusRing: "focus:ring-green-100",
        focusBorder: "focus:border-green-400",
        selectedBg: "bg-green-50/50",
        selectedBorder: "border-green-500",
        selectedText: "text-green-700",
        hoverText: "group-hover:text-green-600",
        checkboxBg: "bg-green-500",
        checkboxBorder: "border-green-500",
        checkboxBorderHover: "group-hover:border-green-300",
        buttonText: "text-green-500 hover:text-green-700 hover:bg-green-50",
        confirmBtn:
          "bg-gradient-to-r from-green-500 to-green-600 hover:to-green-700",
      };
    default:
      return {
        iconBg: "bg-red-100",
        iconText: "text-red-600",
        focusRing: "focus:ring-red-100",
        focusBorder: "focus:border-red-400",
        selectedBg: "bg-red-50/50",
        selectedBorder: "border-red-500",
        selectedText: "text-red-700",
        hoverText: "group-hover:text-red-600",
        checkboxBg: "bg-red-500",
        checkboxBorder: "border-red-500",
        checkboxBorderHover: "group-hover:border-red-300",
        buttonText: "text-red-500 hover:text-red-700 hover:bg-red-50",
        confirmBtn: "bg-gradient-to-r from-red-500 to-red-600 hover:to-red-700",
      };
  }
};

const PracticeInstructorSelectModal = ({
  isOpen,
  onClose,
  onAssign,
  onUnassign,
  student,
  availableInstructors,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInstructorId, setSelectedInstructorId] = useState(null);
  const [filteredInstructors, setFilteredInstructors] = useState([]);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);

  // Invite Mode State
  const [viewMode, setViewMode] = useState("list"); // "list", "invite"
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteResult, setInviteResult] = useState(null); // { link: "..." }

  const studentCategory = categorizeProfession(student?.professionType);
  const theme = getThemeClasses(studentCategory);

  // Pre-select current instructor if assigned
  useEffect(() => {
    // Only use the new activePracticeInstructor field (Single Source of Truth)
    // Legacy 'praxisbildner' field is ignored to prevent duplicate/inconsistent state
    const currentInstructor = student?.activePracticeInstructor;
    if (currentInstructor?.id) {
      setSelectedInstructorId(currentInstructor.id);
    } else {
      setSelectedInstructorId(null);
    }
  }, [student]);

  useEffect(() => {
    if (!availableInstructors) {
      setFilteredInstructors([]);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();

    setFilteredInstructors(
      availableInstructors.filter((instr) => {
        // Filter by profession. An instructor may supervise several professions,
        // so keep them if ANY of their professions matches the student's.
        if (studentCategory !== "OTHER") {
          const instrProfessions = professionsOf(instr);
          const candidates =
            instrProfessions.length > 0
              ? instrProfessions
              : [instr.practiceInstructorSpecialization].filter(Boolean);
          const matches = candidates.some(
            (profession) => categorizeProfession(profession) === studentCategory,
          );
          if (!matches) return false;
        }

        // Search filter
        if (!lowerSearch) return true;
        return (
          instr.firstName.toLowerCase().includes(lowerSearch) ||
          instr.lastName.toLowerCase().includes(lowerSearch) ||
          (instr.email && instr.email.toLowerCase().includes(lowerSearch)) ||
          getInstructorDescriptor(instr).toLowerCase().includes(lowerSearch)
        );
      }),
    );
  }, [availableInstructors, searchTerm, studentCategory]);

  const handleConfirm = () => {
    if (selectedInstructorId) {
      onAssign(selectedInstructorId);
    }
  };

  const handleUnassignClick = () => {
    setShowUnassignConfirm(true);
  };

  const confirmUnassign = () => {
    setShowUnassignConfirm(false);
    onUnassign();
  };

  // Invite logic using standard AuthService
  const handleGenerateInvite = async () => {
    setIsInviting(true);
    setInviteError("");
    setInviteResult(null);

    try {
      // Use existing Invitation API via AuthService
      const payload = {
        role: "PRACTICE_INSTRUCTOR",
        professionType: student.professionType,
        expiresInDays: 7, // Default
        learnerIdToAssign: student.id, // Our added field for auto-assign
      };

      const response = await authService.createInvitation(payload);

      if (response && response.invitationUrl) {
        setInviteResult({
          link: response.invitationUrl,
          expiry: response.expiresAt,
        });
      } else if (response.success && !response.invitationUrl) {
        console.error(
          "Invitation created, but no valid link was returned. Response keys:",
          Object.keys(response),
        );
        throw new Error(
          "Invitation created, but no valid link was returned from the server.",
        );
      } else {
        throw new Error(response.message || "Failed to generate invitation.");
      }
    } catch (err) {
      console.error(err);
      setInviteError(err.message || "Failed to generate invitation.");
    } finally {
      setIsInviting(false);
    }
  };

  const resetInviteState = () => {
    setViewMode("list");
    setInviteError("");
    setInviteResult(null);
  };

  const getInstructorDescriptor = (instructor) =>
    instructor?.practiceInstructorDepartment ||
    instructor?.department ||
    instructor?.practiceInstructorSpecialization ||
    "No department set";

  const handleModalClose = () => {
    onClose();
    resetInviteState();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Unassign Confirmation Modal */}
      {showUnassignConfirm && (
        <Modal
          isOpen
          onClose={() => setShowUnassignConfirm(false)}
          title="Remove assignment?"
          icon="fa-user-slash"
          tone="danger"
          size="md"
          zIndex={10001}
          footer={
            <>
              <ModalSecondaryButton
                onClick={() => setShowUnassignConfirm(false)}
              >
                Cancel
              </ModalSecondaryButton>
              <ModalPrimaryButton onClick={confirmUnassign} tone="danger">
                <i className="fas fa-user-slash"></i>
                Remove assignment
              </ModalPrimaryButton>
            </>
          }
        >
          <p className="text-sm text-slate-cloud leading-relaxed">
            This removes the current practice instructor assignment for{" "}
            <strong className="text-grey-dusk">
              {student?.firstName} {student?.lastName}
            </strong>
            . They can be assigned again at any time.
          </p>
        </Modal>
      )}

      {/* Modal */}
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        title={
          viewMode === "invite"
            ? "Invite new instructor"
            : "Assign practice instructor"
        }
        icon={viewMode === "invite" ? "fa-user-plus" : "fa-user-tag"}
        size="2xl"
        footer={
          viewMode === "list" ? (
            <div className="flex flex-col sm:flex-row justify-between gap-3 w-full">
              <div>
                {onUnassign && (
                  <button
                    type="button"
                    onClick={handleUnassignClick}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-pale-dawn bg-white text-grey-dusk text-sm font-semibold hover:border-sunrise-red/30 hover:text-sunrise-red transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-user-slash"></i>
                    Remove assignment
                  </button>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <ModalSecondaryButton onClick={handleModalClose}>
                  Cancel
                </ModalSecondaryButton>
                <ModalPrimaryButton
                  onClick={handleConfirm}
                  disabled={!selectedInstructorId}
                >
                  <i className="fas fa-check"></i>
                  Confirm assignment
                </ModalPrimaryButton>
              </div>
            </div>
          ) : null
        }
      >
          <div>
            {/* Context Info */}
            <div
              className={`flex items-center text-sm ${theme.iconText} bg-opacity-10 ${theme.iconBg} p-4 rounded-xl border border-transparent`}
            >
              <i className="fas fa-info-circle mr-2"></i>
              {viewMode === "invite" ? (
                <span>
                  Creating invitation for{" "}
                  <strong>
                    {student?.firstName} {student?.lastName}
                  </strong>
                </span>
              ) : (
                <span>
                  Showing instructors for:{" "}
                  <strong>{student?.professionType || "Unknown"}</strong>
                </span>
              )}
            </div>

            {/* Search Bar - Only in List View */}
            {viewMode === "list" && (
              <div className="mt-6 relative">
                <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className={`w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none transition-all focus:ring-2 focus:bg-white ${theme.focusRing} ${theme.focusBorder} text-lg`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8 bg-gray-50/30">
            {viewMode === "invite" ? (
              // INVITE FORM
              <div className="max-w-lg mx-auto">
                {inviteResult ? (
                  <div className="bg-white p-8 rounded-2xl shadow-sm border border-green-100 text-center animate-fade-in-up">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-check text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      Invitation Created!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Share this link with the new instructor. Upon
                      registration, they will be automatically assigned to{" "}
                      <strong>
                        {student?.firstName} {student?.lastName}
                      </strong>
                      .
                    </p>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-6 flex items-center">
                      <input
                        readOnly
                        value={inviteResult.link}
                        className="bg-transparent flex-1 text-gray-600 outline-none text-sm font-mono truncate mr-2"
                      />
                      <button
                        onClick={() =>
                          navigator.clipboard.writeText(inviteResult.link)
                        }
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm whitespace-nowrap"
                      >
                        <i className="far fa-copy mr-1"></i> Copy
                      </button>
                    </div>

                    <button
                      onClick={handleModalClose}
                      className="w-full py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition-colors font-medium"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 animate-fade-in-up">
                    {inviteError && (
                      <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm flex items-start">
                        <i className="fas fa-exclamation-circle mt-0.5 mr-2"></i>
                        {inviteError}
                      </div>
                    )}

                    <div className="text-center mb-8">
                      <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-magic text-2xl"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        Create New Invitation
                      </h3>
                      <p className="text-gray-600">
                        Generate a secure sign-up link for a new{" "}
                        <strong>Practice Instructor</strong>.
                        <br />
                        After registration, they will be automatically assigned
                        to{" "}
                        <strong>
                          {student?.firstName} {student?.lastName}
                        </strong>
                        .
                      </p>
                    </div>

                    <button
                      onClick={handleGenerateInvite}
                      disabled={isInviting}
                      className={`w-full py-4 rounded-xl text-white font-bold text-lg flex items-center justify-center transition-all ${theme.confirmBtn} ${isInviting ? "opacity-70 cursor-wait" : ""}`}
                    >
                      {isInviting ? (
                        <LoadingSpinner size="sm" color="white" />
                      ) : (
                        <>
                          <i className="fas fa-link mr-2"></i> Generate
                          Invitation Link
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setViewMode("list")}
                      className="w-full mt-4 ppx-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : // LIST VIEW
            isLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Invite Card - Always visible */}
                <div
                  onClick={() => setViewMode("invite")}
                  className="relative group cursor-pointer rounded-2xl p-5 transition-all duration-300 border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-white bg-gray-50/50 flex flex-col items-center justify-center text-center min-h-[140px]"
                >
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-3 group-hover:bg-gray-100 group-hover:scale-110 transition-all">
                    <i className="fas fa-plus text-gray-500 group-hover:text-gray-700"></i>
                  </div>
                  <h3 className="font-bold text-gray-700 group-hover:text-gray-900">
                    Invite New Instructor
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Create & Assign Automatically
                  </p>
                </div>

                {filteredInstructors.map((instr) => {
                  const isSelected = selectedInstructorId === instr.id;
                  const descriptor = getInstructorDescriptor(instr);

                  return (
                    <div
                      key={instr.id}
                      onClick={() => setSelectedInstructorId(instr.id)}
                      className={`
                                                    relative group cursor-pointer rounded-2xl p-5 transition-all duration-300 border-[3px]
                                                    ${
                                                      isSelected
                                                        ? `${theme.selectedBg} ${theme.selectedBorder} shadow-lg transform scale-[1.03]`
                                                        : "bg-white border-transparent hover:border-gray-200 hover:shadow-xl hover:-translate-y-1"
                                                    }
                                                    shadow-sm
                                                `}
                    >
                      <div className="flex items-start">
                        <div className="flex-1 min-w-0">
                          <h3
                            className={`font-bold text-gray-800 truncate transition-colors ${theme.hoverText} ${isSelected ? theme.selectedText : ""}`}
                          >
                            {instr.firstName} {instr.lastName}
                          </h3>
                          <p className="text-sm text-gray-500 truncate mb-2">
                            <i className="far fa-envelope mr-1.5 opacity-75"></i>
                            {instr.email}
                          </p>

                          {/* Badges */}
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                              {descriptor}
                            </span>
                            {instr.maxStudents && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100">
                                Capacity: {instr.maxStudents}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Selection Indicator */}
                        <div
                          className={`
                                                        w-8 h-8 rounded-full border-[3px] flex items-center justify-center transition-colors shrink-0 ml-3
                                                        ${
                                                          isSelected
                                                            ? `${theme.checkboxBg} ${theme.checkboxBorder}`
                                                            : `border-gray-200 ${theme.checkboxBorderHover}`
                                                        }
                                                    `}
                        >
                          {isSelected && (
                            <i className="fas fa-check text-white text-sm font-bold"></i>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
      </Modal>
    </>
  );
};

export default PracticeInstructorSelectModal;
